import { useForm, useWatch } from "react-hook-form";
import { formValues, schema } from "./validations";
import { ACK, ACK_AT, ACK_BY, ACKNOWLEDGED, AMOUNT_RECEIVED, CHEQUE_NO, DEFAULT_AMOUNT, FEE_AMOUNT, FEE_TYPE_ID, FEES, IS_SELECTED, OCP_ID, PROPERTY_ID, RECEIPT_ID, RECEIPT_PROOF, RECEIPTS, RECEIVED_DATE, STATUS } from "../../consts";
import { zodResolver } from "@hookform/resolvers/zod";
import { getRequiredFields } from "@/lib/utils";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { OCP } from "../../../consts";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useGetData } from "@/services";
import { toast } from "@/components/Toast";

const useStep2 = ({onBaseSubmit}: {onBaseSubmit: any}) => {
  const ocpData = useSelector((state: RootState) => state.currentData.ocpData);
  const communityData = useSelector((state: RootState) => state.currentData.communityData);
  const loggedInUserData = useSelector((state: RootState) => state.currentData.loggedInUserData);
  const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});

  const form = useForm<formValues>({
    defaultValues: {
      // step 2
      [OCP_ID]: ocpData?.[OCP]?.toString(),
      [PROPERTY_ID]: ocpData?.[PROPERTY_ID]?.toString(),
      [FEES]: [],
      [RECEIPTS]: [{
        [RECEIPT_ID]: "",
        [CHEQUE_NO]: "",
        [RECEIVED_DATE]: undefined,
        [AMOUNT_RECEIVED]: "",
        [RECEIPT_PROOF]: undefined,
        [ACK]: false,
        [ACK_AT]: undefined,
        [ACK_BY]: "",
      }],
      [STATUS]: "FEES_CAPTURED"
    },
    resolver: zodResolver(schema),
  });
  const formData: any = useWatch({ control: form.control });
  const requiredFields = getRequiredFields(schema);
  const [fileKey, setFileKey] = useState<number>(Math.random() * 2);

  useEffect(() => {
    if (ocpData) {
      form.setValue(OCP_ID, ocpData?.[OCP]?.toString());
      form.setValue(PROPERTY_ID, ocpData?.[PROPERTY_ID]?.toString());
    }
  }, [ocpData]);

  const onAddMore = () => {
    const receipts = [
      ...(formData[RECEIPTS] || []),
      {
        [RECEIPT_ID]: "",
        [CHEQUE_NO]: "",
        [RECEIVED_DATE]: undefined,
        [AMOUNT_RECEIVED]: "",
        [RECEIPT_PROOF]: undefined,
        [ACK]: false,
        [ACK_AT]: undefined,
        [ACK_BY]: "",
      },
    ];
  
    form.setValue(RECEIPTS, receipts, { shouldDirty: true, shouldValidate: true });
  };

  const onRemove = (childIndexToRemove: number) => () => {
    form.setValue(RECEIPTS, formData[RECEIPTS]?.filter((_: any, idx: number) => idx !== childIndexToRemove), {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  const { data: feesData, isLoading: isDataLoading } = useGetData({
    communityId: communityData?.communityId,
    module: 'fee-types' as string,
    isCommunityQuery: true,
    queryParams: { enabled: !!communityData?.communityId },
  });

  useEffect(() => {
    if (feesData?.data) {
      const feeList = feesData?.data?.map((data: any) => {
        const currFee = ocpData?.[FEES]?.find((fee: any) => fee?.[FEE_TYPE_ID]?.toString() == data?.[FEE_TYPE_ID]?.toString() && fee?.[IS_SELECTED]);

        return {
          ...data, 
          ...currFee,
          [FEE_AMOUNT]: currFee ? currFee?.[FEE_AMOUNT] : data?.[DEFAULT_AMOUNT],
          [IS_SELECTED]: currFee ? currFee?.[IS_SELECTED] : false
        }
      })      
      form.setValue(FEES, feeList);
    }
  }, [feesData, ocpData]);
  
  useEffect(() => {
    if (ocpData) {
      const receipts = (ocpData?.[RECEIPTS] || ocpData?.feeSummary?.[RECEIPTS])?.map((val: any) => ({...val, [ACK]: val?.status == ACKNOWLEDGED, [RECEIPT_PROOF]: val?.proofAttachments?.length ? {name: val?.proofAttachments?.[0]?.originalFileName, size: val?.proofAttachments?.[0]?.fileSize} : undefined}));
      form.setValue(RECEIPTS, receipts?.length ? receipts : [{
          [RECEIPT_ID]: "",
          [CHEQUE_NO]: "",
          [RECEIVED_DATE]: undefined,
          [AMOUNT_RECEIVED]: "",
          [RECEIPT_PROOF]: undefined,
          [ACK]: false,
          [ACK_AT]: undefined,
          [ACK_BY]: "",
      }]);
      setFileKey(Math.random() * 2);
    }
  }, [ocpData])

  const fees = useWatch({ control: form.control, name: FEES });

  const isAmountReceived = useCallback(() => {
      const total = (formData?.[FEES] || []).filter((data: any) => data?.[IS_SELECTED]).reduce((acc: number, val: any) => {
        const num = Number(val?.[FEE_AMOUNT]);
        return acc + (isNaN(num) ? 0 : num);
      }, 0);

      const received = (formData?.[RECEIPTS] || []).reduce((acc: number, val: any) => {
        const num = Number(val?.[AMOUNT_RECEIVED]);
        return acc + (isNaN(num) ? 0 : num);
      }, 0);
  
      return !isNaN(total) && total === received;
    },
    [formData]
  );

  const summary = useMemo(() => {
    const total = (formData?.[FEES] || []).filter((data: any) => data?.[IS_SELECTED])?.reduce((acc: number, val: any) => {
      const num = Number(val?.[FEE_AMOUNT]);
      return acc + (isNaN(num) ? 0 : num);
    }, 0);

    const received = (formData?.[RECEIPTS] || []).reduce((acc: number, val: any) => {
      const num = Number(val?.[AMOUNT_RECEIVED]);
      return acc + (isNaN(num) ? 0 : num);
    }, 0);

    return {total, received, outstanding: total - received};
  }, [formData[FEES]]);

  const onSubmit = (data: any) => {
    let errors = '';

    // 1. Get all selected fees
    const selectedFees = data?.[FEES]?.filter((fee: any) => fee?.[IS_SELECTED]) || [];

    // 2. Validation: Check if NO fee is selected
    if (selectedFees.length === 0) {
      toast.error("Please select at least one fee to continue.");
      return;
    }

    // 3. Validation: Check if any selected fee is missing its amount
    let hasMissingAmount = false;
    data?.[FEES]?.forEach((fee: any) => {
      // Replace FEE_AMOUNT with your actual amount constant (e.g., AMOUNT, RATE, etc.)
      if (fee?.[IS_SELECTED] && !fee?.[FEE_AMOUNT]) {
        hasMissingAmount = true;
      }
    });

    if (hasMissingAmount) {
      toast.error("Fee amount is required for all selected fee(s).");
      return;
    }

    if (Number(summary.outstanding) < 0) {
      toast.error("Amount received must equal the total amount.");
      return;
    }

    data?.[RECEIPTS]
      ?.forEach((val: any, index: number) => {
        if (val?.[RECEIPT_ID]) return;
        // Check if ANY field in this row has been filled
        const hasAnyValue = 
          Boolean(val[CHEQUE_NO]) ||
          Boolean(val[RECEIVED_DATE]) ||
          Boolean(val[AMOUNT_RECEIVED]) ||
          Boolean(val[RECEIPT_PROOF]) || Boolean(val[ACK]);

        // Check if ALL mandatory fields in this row are filled
        const hasAllValues = 
          Boolean(val[CHEQUE_NO]) &&
          Boolean(val[RECEIVED_DATE]) &&
          Boolean(val[AMOUNT_RECEIVED]) && 
          Boolean(val[RECEIPT_PROOF]) && Boolean(val[ACK])

        // If the user started filling the row but left mandatory fields blank, throw or flag an error
        if (hasAnyValue && !hasAllValues) {
          errors += `\nIncomplete row ${index + 1}: Please fill all required fields (Cheque Number, Date, and Amount, Receipt Proof, Acknowledgement)`;
        }
    })

    if (errors) {
      form.setError(RECEIPTS, {message: errors});
      return;
    }

    onBaseSubmit({
      ...data,
      [FEES]: data?.[FEES]?.map((fee: any) => ({ ...fee, [FEE_AMOUNT]: fee?.[FEE_AMOUNT] || "0"})),
      [RECEIPTS]: data[RECEIPTS]?.filter((val: any) => val[AMOUNT_RECEIVED] && !val[RECEIPT_ID])?.map((val: any) => ({...val, [ACK]: val?.[ACK] ? ACKNOWLEDGED : ""})),
      draft: !!summary?.outstanding
    })
  }

  const isFeeSelected = useMemo(() => fees?.some((data: any) => data?.[IS_SELECTED] && Number(data?.[FEE_AMOUNT]) > 0), [fees]);

  return {
    form,
    requiredFields,
    formData,
    onAddMore,
    isAmountReceived,
    isDataLoading,
    onRemove,
    loggedInUserData,
    onSubmit,
    fileInputRefs,
    summary,
    fileKey,
    isFeeSelected
  }
}

export default useStep2;